import { getCroppedImg } from "../../utils/profileImage";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Cropper from "react-easy-crop";
import {
  Sparkles,
  Plus,
  ArrowLeft,
  Loader2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "./UserDetailsEditTwoPage.css";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import appIcon from "../../assets/appIcon.svg";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import { useGlobalAlert } from "../../context/AlertContext";
import userPlaceholder from "../../assets/userPlaceholder.png";
import { updateProfileInStore } from "../../redux/userSlice";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";





const UserDetailsEditTwoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  // Mobile Step State (1 for Left Panel, 2 for Right Panel)
  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  const [loading, setLoading] = useState(true);
  const [loadingBio, setLoadingBio] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [userData, setUserData] = useState(null);

  const [formData, setFormData] = useState({
    userBio: "",
    dogName: "",
    photos: { 0: null, 1: null, 2: null, 3: null },
    whatsBringsYou: "",
  });

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropIndex, setCropIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const imageRefs = useRef([]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const u = res.data.data;
          setUserData(u);

          const userPics =
            u.user_profile_image || u?.dogs?.[0]?.profile_image || [];
          const dog = u.dogs?.[0] || {};

          setFormData({
            userBio: u.user_bio || "",
            dogName: dog.dog_name || "Dog",
            photos: {
              0: userPics[0] || null,
              1: userPics[1] || null,
              2: userPics[2] || null,
              3: userPics[3] || null,
            },
            whatsBringsYou: u.whats_brings_you,
          });
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const handleImageSelect = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setCropImageSrc(reader.result);
        setCropIndex(index);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCropModalOpen(true);
      };
      event.target.value = "";
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedFile = await getCroppedImg(
        cropImageSrc,
        croppedAreaPixels,
        cropIndex,
      );
      if (croppedFile) {
        setFormData((prev) => ({
          ...prev,
          photos: { ...prev.photos, [cropIndex]: croppedFile },
        }));
      }
    } catch (e) {
      console.error("Error generating crop", e);
    } finally {
      setCropModalOpen(false);
      setCropImageSrc(null);
    }
  };

  const handleCancelCrop = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
  };

  const getPreviewUrl = (photo) => {
    if (!photo) return userPlaceholder;
    if (typeof photo === "string") return photo;
    return URL.createObjectURL(photo);
  };

  const handleBioChange = (e) => {
    setFormData((prev) => ({ ...prev, userBio: e.target.value }));
  };

  // Validation
  const hasAtLeastOneImage = () => {
    if (!formData.photos) return false;
    return ["0", "1", "2", "3"].some((key) => formData.photos[key]);
  };

  const isBioValid = () => {
    return formData.userBio && formData.userBio.trim().length > 0;
  };

  const isStep1Valid = hasAtLeastOneImage();
  const isStep2Valid = isBioValid();
  const isFinalNextEnabled = isStep1Valid && isStep2Valid && !isUpdating;

  const handleNext = async () => {
    if (window.innerWidth >= 1024) {
      if (!isFinalNextEnabled) return;
    } else {
      if (!isStep2Valid || isUpdating) return;
    }

    setIsUpdating(true);

    try {
      // Update Bio details
      const data = new FormData();
      data.append("user_bio", formData.userBio);

      const res = await apiClient.put(
        `/user/update-user-byId/${profile?.id}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      // Update Images using the new endpoint
      const imageFormData = new FormData();
      const changedIndexes = [];

      // Loop through all 4 image slots
      for (let i = 0; i < 4; i++) {
        const item = formData.photos[i];

        if (item instanceof File) {
          imageFormData.append("user_profile_image", item);
          changedIndexes.push(i);
        }
      }

      if (changedIndexes.length > 0) {
        imageFormData.append("indexes", JSON.stringify(changedIndexes));

        await apiClient.put(
          `/user/update-user-images-by-index/${profile?.id}`,
          imageFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
      }

      if (res.data?.status) {
        triggerAlert("Profile updated successfully!", "success");
        navigate("/user-profile");
      }
    } catch (err) {
      console.error("Update failed:", err);
      triggerAlert("Failed to update profile.", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWriteBio = async () => {
    if (!userData?.dogs?.[0]) return;
    setLoadingBio(true);
    try {
      const payload = {
        name: userData.user_name,
        what_brings_you_here: formData.whatsBringsYou,
      };

      const response = await apiClient.post(
        ENDPOINTS.GENERATE_AI_BIO_TEAMSOCIAL,
        payload,
      );
      if (response.data.success && response.data.bio) {
        setFormData((prev) => ({
          ...prev,
          userBio: response.data.bio,
        }));
      }
    } catch (error) {
      triggerAlert("Could not generate bio at this time.", "error");
    } finally {
      setLoadingBio(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div
      className={`udete-container ${mobileStep === 1 ? "udete-mobile-step-1" : "udete-mobile-step-2"}`}
    >
      {/* LEFT PANEL (Green/Photos) - Top on Mobile / Step 1 */}
      <div
        className={`udete-left-panel ${mobileStep === 1 ? "udete-mobile-active" : "udete-mobile-hidden"}`}
      >
        {/* Mobile Header (Hidden on Desktop) */}
        {/* <div className="udete-mobile-header udete-mobile-only">
          <button
            className="udete-back-btn"
            onClick={() => {
              if (window.innerWidth < 1024) {
                navigate("/edit-user-details", {
                  state: { step: 2 },
                });
              } else {
                navigate(-1);
              }
            }}
          >
            <ArrowLeft size={22} color="#000" strokeWidth={3} />
            <span style={{ color: "#000" }}>Back</span>
          </button>
          <div className="udete-header-logo" style={{ color: "#009fd9" }}>
            <img
              src={socialPawsLogo}
              alt="Social Paws"
              className="udete-logo-img"
            />
            <span>Social Paws</span>
          </div>
        </div> */}
        <MobileTopBar
          onBack={() => {
            if (window.innerWidth < 1024) {
              navigate("/edit-user-details", {
                state: { step: 2 },
              });
            } else {
              navigate(-1);
            }
          }}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />

        {/* Desktop Back Button */}
        {/* <button
          className="login-back-btn udete-desktop-only"
          onClick={() => navigate("/edit-user-details")}
        >
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span style={{ color: "#000" }}>Back</span>
        </button> */}
        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/edit-user-details")}
          backColor="#000"
        />

        <div className="udete-content-wrapper centered-text">
          <div className="udete-header-group">
            <h1 className="udete-title text-black">YOUR PICS</h1>
            <p className="udete-subtitle text-black">
              A happy headshot, a family pic with {formData.dogName || "Dimsum"}
              , and doing what you do in the world, are all great choices!
            </p>
          </div>

          {/* PHOTO GRID */}
          <div className="udete-photo-grid">
            {/* Main Large Image */}
            <div
              className="udete-photo-box udete-main-photo"
              onClick={() => imageRefs.current[0].click()}
            >
              <input
                type="file"
                hidden
                ref={(el) => (imageRefs.current[0] = el)}
                accept="image/*"
                onChange={(e) => handleImageSelect(e, 0)}
              />
              {formData.photos[0] ? (
                <img
                  src={getPreviewUrl(formData.photos[0])}
                  alt="Main Upload"
                  className="udete-uploaded-img"
                />
              ) : (
                <Plus size={40} color="#000" />
              )}
            </div>

            {/* Sub Images Row */}
            <div className="udete-sub-photos-row">
              {[1, 2, 3].map((index) => (
                <div
                  key={index}
                  className="udete-photo-box udete-sub-photo"
                  onClick={() => imageRefs.current[index].click()}
                >
                  <input
                    type="file"
                    hidden
                    ref={(el) => (imageRefs.current[index] = el)}
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, index)}
                  />
                  {formData.photos[index] ? (
                    <img
                      src={getPreviewUrl(formData.photos[index])}
                      alt={`Sub ${index}`}
                      className="udete-uploaded-img"
                    />
                  ) : (
                    <Plus size={32} color="#000" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Only Step 1 Footer */}
          {/* <div className="udete-mobile-footer udete-mobile-only">
            <button
              className="udete-skip-btn"
              onClick={() => navigate("/user-profile")}
              style={{ color: "#009fd9" }}
            >
              EXIT
            </button>
            <button
              onClick={() => isStep1Valid && setMobileStep(2)}
              className={`udete-next-btn-group ${
                isStep1Valid ? "enabled" : "disabled"
              }`}
            >
              <span className="udete-next-text" style={{ color: "#009fd9" }}>
                NEXT
              </span>
              <div
                className="udete-next-circle"
                style={{ backgroundColor: "rgba(0, 159, 217, 0.15)" }}
              >
                <img
                  src={nextIcon2}
                  alt="Next Icon"
                  className="udete-next-icon"
                />
              </div>
            </button>
          </div> */}
          <MobileBottomBar
            onNext={() => isStep1Valid && setMobileStep(2)}
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            showSkip={true}
            isSkipBlue={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL (Blue/Bio) - Bottom on Mobile / Step 2 */}
      <div
        className={`udete-right-panel ${mobileStep === 2 ? "udete-mobile-active" : "udete-mobile-hidden"}`}
      >
        {/* Mobile Header (Hidden on Desktop) */}
        {/* <div className="udete-mobile-header udete-mobile-only">
          <button className="udete-back-btn" onClick={() => setMobileStep(1)}>
            <ArrowLeft size={22} color="#fff" strokeWidth={3} />
            <span style={{ color: "#fff" }}>Back</span>
          </button>
          <div className="udete-header-logo" style={{ color: "#fff" }}>
            <img src={appIcon} alt="Social Paws" className="udete-logo-img" />
            <span>Social Paws</span>
          </div>
        </div> */}
        <MobileTopBar
          onBack={() => setMobileStep(1)}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="udete-content-wrapper">
          <h1 className="udete-title text-white">IN YOUR OWN WORDS</h1>

          <p className="udete-subtitle text-light-blue">
            Share more about what brings you to Social Paws,
            {formData.dogName ? ` ${formData.dogName}'s` : " [Dog Name]'s"}{" "}
            greatest traits, and whatever else your heart desires!
          </p>

          <div className="udete-bio-wrapper">
            <textarea
              className="udete-bio-textarea"
              placeholder="Write here..."
              value={formData.userBio}
              onChange={handleBioChange}
            />
            <button
              type="button"
              className="udete-write-btn"
              onClick={handleWriteBio}
              disabled={loadingBio}
            >
              {loadingBio ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Sparkles size={20} />
              )}
              {loadingBio ? "GENERATING..." : "WRITE MY BIO"}
            </button>
            <p
              className="iyow-ai-help-text"
              style={{ marginTop: window.innerWidth >= 1024 ? "" : "-1rem" }}
            >
              Want a helping paw?
              <br />
              Let AI write the bio!
              <br />
              you can edit before saving
            </p>
          </div>

          {/* Desktop Footer */}
          <div className="udete-footer udete-desktop-only">
            <button
              className="udete-skip-btn"
              onClick={() => navigate("/user-profile")}
            >
              EXIT
            </button>

            <button
              onClick={isFinalNextEnabled ? handleNext : undefined}
              className={`udete-next-btn-group ${
                !isFinalNextEnabled || isUpdating ? "disabled" : ""
              }`}
            >
              <span
                className="udete-next-text"
                style={{ color: "rgba(182, 252, 238, 1)" }}
              >
                {isUpdating ? "UPDATING..." : "FINISH"}
              </span>
              <div
                className="udete-next-circle"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
              >
                {isUpdating ? (
                  <Loader2 className="animate-spin" size={20} color="#fff" />
                ) : (
                  <img
                    src={nextIcon}
                    alt="Next Icon"
                    className="udete-next-icon"
                  />
                )}
              </div>
            </button>
          </div>

          {/* Mobile Step 2 Footer */}
          {/* <div className="udete-mobile-footer udete-mobile-only udete-step-2-next">
            <button
              className="udete-skip-btn"
              onClick={() => navigate("/user-profile")}
              style={{ color: "#fff" }}
            >
              EXIT
            </button>
            <button
              onClick={isStep2Valid && !isUpdating ? handleNext : undefined}
              className={`udete-next-btn-group ${
                !isStep2Valid || isUpdating ? "disabled" : ""
              }`}
            >
              <span className="udete-next-text" style={{ color: "#47d7ac" }}>
                {isUpdating ? "UPDATING..." : "FINISH"}
              </span>
              <div
                className="udete-next-circle"
                style={{ backgroundColor: "rgba(71, 215, 172, 0.15)" }}
              >
                {isUpdating ? (
                  <Loader2 className="animate-spin" size={20} color="#fff" />
                ) : (
                  <img
                    src={nextIcon}
                    alt="Next Icon"
                    className="udete-next-icon"
                  />
                )}
              </div>
            </button>
          </div> */}
          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isStep2Valid && !isUpdating}
            nextIconSrc={nextIcon}
            showSkip={true}
            skipText="EXIT"
            nextText="FINISH"
            onSkip={() => navigate("/user-profile")}
            variant="green"
          />
        </div>
      </div>

      {/* Custom Cropper Modal */}
      {cropModalOpen && cropImageSrc && (
        <div className="crop-modal-overlay">
          <div className="crop-modal-box">
            <button className="crop-close-btn" onClick={handleCancelCrop}>
              <X size={20} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
            </button>

            <h2 className="crop-modal-title">Upload Picture</h2>

            <div className="crop-container">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                classes={{
                  containerClassName: "custom-cropper-container",
                }}
                cropAreaStyle={{
                  border: "2px dashed white",
                }}
              />
            </div>

            <div className="crop-slider-wrapper">
              <ZoomOut size={24} color="#000" />
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(e.target.value)}
                className="crop-slider"
              />
              <ZoomIn size={24} color="#000" />
            </div>

            <div className="crop-action-row">
              <button
                className="crop-btn-change"
                onClick={() => {
                  imageRefs.current[cropIndex].click();
                }}
              >
                CHANGE IMAGE
              </button>
              <button className="crop-btn-save" onClick={handleSaveCrop}>
                CROP & SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailsEditTwoPage;
